// ============================================================
// src/context/AuthContext.js
// ✅ Per-hospital token isolation
// Each hospital stores its own token in localStorage
// token_pearlsmile, token_sai-dental-hospital etc.
// ============================================================
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, hospitalId, hospitalSlug }) => {
  // ✅ Each hospital uses its own token key
  const tokenKey = hospitalSlug
    ? `pearlsmile_token_${hospitalSlug}`
    : "pearlsmile_token";

  const [doctor, setDoctor] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey));

  useEffect(() => {
    const stored = localStorage.getItem(tokenKey);
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split(".")[1]));
        if (payload.exp * 1000 > Date.now()) {
          setDoctor(payload);
          setToken(stored);
        } else {
          // Expired — clear this hospital's token only
          localStorage.removeItem(tokenKey);
          setDoctor(null);
          setToken(null);
        }
      } catch {
        localStorage.removeItem(tokenKey);
        setDoctor(null);
        setToken(null);
      }
    } else {
      setDoctor(null);
      setToken(null);
    }
  }, [tokenKey]); // ✅ Re-run when hospital changes

  const login = (newToken, doctorData) => {
    // ✅ Save token under THIS hospital's key only
    localStorage.setItem(tokenKey, newToken);
    setToken(newToken);
    setDoctor(doctorData);
  };

  const logout = () => {
    // ✅ Only remove THIS hospital's token
    localStorage.removeItem(tokenKey);
    setToken(null);
    setDoctor(null);
  };

  return (
    <AuthContext.Provider
      value={{
        doctor,
        token,
        login,
        logout,
        isLoggedIn: !!doctor,
        hospitalId: hospitalId || null,
        hospitalSlug: hospitalSlug || null,
        tokenKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
